import { FormControl, FormHelperText, MenuItem, Select, Typography } from '@mui/material';
import {
  Controller,
  useFormContext,
  type FieldPathByValue,
  type FieldPathValue,
  type RegisterOptions,
} from 'react-hook-form';

import type { PropertyAdEditorFormValues } from '../../../types/propertyAdEditor';

import { LabelComponent } from '../../uiComponents/LabelComponent';
import styles from '../PropertyAdEditor.module.scss';

type EditorSelectFieldName = FieldPathByValue<PropertyAdEditorFormValues, string | null>;
type EditorSelectFieldValue<TName extends EditorSelectFieldName> = Extract<
  NonNullable<FieldPathValue<PropertyAdEditorFormValues, TName>>,
  string
>;

export interface EditorSelectOption<TValue extends string> {
  label: string;
  value: TValue;
}

interface EditorSelectFieldProps<TName extends EditorSelectFieldName> {
  ariaLabel?: string;
  disabled?: boolean;
  label: string;
  name: TName;
  onValueChange?: (value: EditorSelectFieldValue<TName> | null) => void;
  options: ReadonlyArray<EditorSelectOption<EditorSelectFieldValue<TName>>>;
  placeholder?: string;
  required?: boolean;
  rules?: RegisterOptions<PropertyAdEditorFormValues, TName>;
}

export const EditorSelectField = <TName extends EditorSelectFieldName>({
  ariaLabel,
  disabled = false,
  label,
  name,
  onValueChange,
  options,
  placeholder,
  required = false,
  rules,
}: EditorSelectFieldProps<TName>) => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const errorId = `${name}-error`;

  return (
    <Controller<PropertyAdEditorFormValues, TName>
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormControl
          className={styles.editorFieldControl}
          disabled={disabled}
          error={fieldState.invalid}
        >
          <LabelComponent isRequired={required} label={label} />
          <Select<EditorSelectFieldValue<TName> | ''>
            className={styles.selectControl}
            displayEmpty={Boolean(placeholder)}
            inputRef={field.ref}
            SelectDisplayProps={{
              'aria-describedby': fieldState.error ? errorId : undefined,
              'aria-label': ariaLabel ?? label,
              'aria-invalid': fieldState.invalid,
            }}
            name={field.name}
            value={(field.value ?? '') as EditorSelectFieldValue<TName> | ''}
            renderValue={(selectedValue) => (
              <Typography
                className={selectedValue === '' ? styles.selectPlaceholder : styles.selectValue}
                component='span'
                variant='inherit'
              >
                {selectedValue === ''
                  ? placeholder
                  : options.find((option) => option.value === selectedValue)?.label ??
                    selectedValue}
              </Typography>
            )}
            onBlur={field.onBlur}
            onChange={(event) => {
              const nextValue = (
                event.target.value === '' ? null : event.target.value
              ) as EditorSelectFieldValue<TName> | null;

              if (onValueChange) {
                onValueChange(nextValue);
                return;
              }

              field.onChange(nextValue);
            }}
          >
            {placeholder && (
              <MenuItem disabled value=''>
                {placeholder}
              </MenuItem>
            )}
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {fieldState.error?.message && (
            <FormHelperText id={errorId}>{fieldState.error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
